import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Field, SingleResult} from '@app/interfaces';
import {FormBuilder, FormGroup} from '@angular/forms';
import {FormHelperService} from '@app/util/form-helper.service';
import {KeyboardService} from '@app/services/keyboard.service';
import {Observable, Subscription} from 'rxjs';
import {InlineFieldEventStatus, InlineFieldService} from '@app/services/inline-field.service';
import {FieldCurrencyFormDialogComponent} from '@app/dialogs/field-currency-form-dialog/field-currency-form-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import {FieldApiService} from '@app/services/api/field-api.service';
import {FocusService} from "@app/services/util/focus.service";
import {FeatureFlagService} from "@app/services/feature-flag.service";

@Component({
    selector: 'app-inline-field-edit',
    templateUrl: './inline-field-edit.component.html',
    styleUrls: ['./inline-field-edit.component.scss'],
    standalone: false
})
export class InlineFieldEditComponent implements OnInit, OnChanges, OnDestroy {

  @Input() field: Field<any>;
  @Input() isHiddenRepeaterField = false;
  @Input() isSaving = false;
  @Input() cancelEditOnSave = false;
  @Input() textSize = 'lg';
  @Input() hideLabel = false;
  @Input() replaceNullValueWith = '-';
  @Input() removeBoldFromValue = false;
  @Input() expandToFullSize = false;
  @Input() getFieldApi: { getField(entityUuid: string, fieldUuid: string): Observable<SingleResult<Field<any>>> };
  @Input() entityUuid: string;
  @Input() minWidth: string;
  @Input() updateField = true;
  @Input() subFields: Array<Field<any>>;
  @Input() isHidden = false;
  @Input() isEditable = true;
  @Input() formGroup: FormGroup;

  @Output('update') onUpdate = new EventEmitter<Field<any>>();
  @Output('updateWithDescription') onUpdateWithDescription = new EventEmitter<{field: Field<any>, reason: string, description: string}>();

  public fieldEditMap: Map<string, FieldEdit> = new Map();
  public showPriceChangeDescription = false;

  private keyboardSubscription: Subscription;
  private inlineEditEventsSubscription: Subscription;
  public originalFieldValue: unknown;
  private priceChangeReasonRequired = false;

  constructor(private formBuilder: FormBuilder,
              private formBuilderHelper: FormHelperService,
              private inlineFieldService: InlineFieldService,
              private matDialog: MatDialog,
              private fieldApi: FieldApiService,
              private keyboardService: KeyboardService,
              private formHelperService: FormHelperService,
              private focusService: FocusService,
              private featureFlagService: FeatureFlagService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.field) {
      if (changes.field.previousValue !== changes.field.currentValue) {
        this.fieldEditMap = new Map();
        this.cancelSubscriptions();
      }
    }
  }

  ngOnInit(): void {
    if (!this.isHiddenRepeaterField && this.formGroup && 'value' in this.formGroup && this.formGroup.value[this.field.fieldUuid]) {
      this.field.value = this.formGroup.value[this.field.fieldUuid];
    }

    if (this.formGroup && this.formGroup.controls[this.field.fieldUuid]) {
      // @ts-ignore
      this.isHidden = this.formGroup.controls[this.field.fieldUuid].hidden;

      this.formGroup.controls[this.field.fieldUuid].valueChanges.subscribe((x) => {
        // @ts-ignore
        this.isHidden = this.formGroup.controls[this.field.fieldUuid].hidden;
      });
    }

    this.formHelperService.modifyOnSetFields(this.field.value, this.field, this.formGroup);
    
    // Fetch feature flag for price change description
    this.featureFlagService.getFeatureFlags().subscribe((flags) => {
      this.priceChangeReasonRequired = flags?.['priceChangeReasonRequired'] ?? false;
    });
  }

  ngOnDestroy(): void {
    this.cancelSubscriptions();
  }

  enableEdit(field: Field<any>) {
    this.originalFieldValue = field.value;
    const isAdvertisedPriceField = field.config?.typeId === 'briefd.vehicle.advertised_sell_price_in_eur';
    const hasChoices = field.config?.priceChangeReasonChoices && Object.keys(field.config.priceChangeReasonChoices).length > 0;
    this.showPriceChangeDescription = isAdvertisedPriceField && this.priceChangeReasonRequired && hasChoices;

    if (this.getFieldApi) {
      this.getFieldApi.getField(this.entityUuid, field.fieldUuid).subscribe((result) => {
        const fetchedField = result.data;
        this.originalFieldValue = field.value;
        const controls = {};
        controls[field.fieldUuid] = this.formBuilderHelper.prepareFormGroupControlForFieldItem(fetchedField);

        // Create new formGroup for advertised price so the selector component can add its controls
        const formGroup = (this.formGroup && !this.showPriceChangeDescription) ? this.formGroup : this.formBuilder.group(controls);
        this.fieldEditMap.set(field.fieldUuid, {
          originalField: field,
          updatedField: Object.assign({}, fetchedField),
          formGroup
        });

        this.inlineFieldService.fire(field.fieldUuid, InlineFieldEventStatus.opened);
        this.setupListener(field);
      });
    } else {
      const controls = {};
      controls[field.fieldUuid] = this.formBuilderHelper.prepareFormGroupControlForFieldItem(field);

      // Create new formGroup for advertised price so the selector component can add its controls
      const formGroup = (this.formGroup && !this.showPriceChangeDescription) ? this.formGroup : this.formBuilder.group(controls);

      this.fieldEditMap.set(field.fieldUuid, {originalField: field, updatedField: Object.assign({}, field), formGroup});

      this.inlineFieldService.fire(field.fieldUuid, InlineFieldEventStatus.opened);
      this.setupListener(field);
    }

    this.formGroup?.controls[field.fieldUuid].setErrors({'incorect': true});
  }

  private setupListener(field: Field<any>) {
    // Skip keyboard shortcuts for advertised price (textarea needs Enter for line breaks)
    if (!this.showPriceChangeDescription) {
      this.keyboardSubscription = this.keyboardService.events().subscribe((event) => {
        //  TODO: HACK! Vehicle model should not be automatically confirmed when pressing enter as it breaks it
        if (event.key.toLowerCase() === 'enter' && field.type !== 'vehicle_model' && field.type !== 'text_rich') {
          this.saveEdit(field);
        } else if (event.key.toLowerCase() === 'escape') {
          this.cancelEdit(field);
        }
      });
    }

    //  Remove all subscriptions if we opened/closed a different inline field
    this.inlineEditEventsSubscription = this.inlineFieldService.events().subscribe((event) => {
      if (event.fieldUuid !== field.fieldUuid) {
        this.cancelSubscriptions();
      }
    });
  }

  cancelEdit(field: Field<any>) {
    this.fieldEditMap.delete(field.fieldUuid);
    this.inlineFieldService.fire(field.fieldUuid, InlineFieldEventStatus.closed);
    this.showPriceChangeDescription = false;

    this.cancelSubscriptions();
    this.formGroup?.controls[field.fieldUuid].setErrors(null);
    // Reset to the original value
    this.formGroup?.controls[field.fieldUuid].setValue(this.originalFieldValue);
    this.field.value = this.originalFieldValue;
  }

  saveEdit(field: Field<any>) {
    this.formGroup?.controls[field.fieldUuid].setErrors(null);
    const fieldEdit = this.fieldEditMap.get(field.fieldUuid);
    fieldEdit.updatedField.value = fieldEdit.formGroup.controls[field.fieldUuid].value;

    if (fieldEdit.formGroup.controls[field.fieldUuid].valid) {
      const previousValue = this.originalFieldValue;
      this.originalFieldValue = fieldEdit.updatedField.value;
      this.field.value = fieldEdit.updatedField.value;
      fieldEdit.updatedField.previousValue = previousValue;
      
      // Emit with reason + description for advertised price field
      if (this.showPriceChangeDescription && fieldEdit.formGroup.controls['priceChangeReason']) {
        const reason = fieldEdit.formGroup.controls['priceChangeReason'].value;
        const description = fieldEdit.formGroup.controls['priceChangeDescription']?.value || '';
        this.onUpdateWithDescription.emit({
          field: fieldEdit.updatedField,
          reason,
          description
        });
      } else {
        this.onUpdate.emit(fieldEdit.updatedField);
      }
    }

    if (this.cancelEditOnSave) {
      this.cancelEdit(field);
    }
  }

  getClass() {
    return {
      'text-lg': this.textSize === 'lg',
      'text-sm': this.textSize === 'sm',
    };
  }

  private cancelSubscriptions() {
    if (this.keyboardSubscription) {
      this.keyboardSubscription.unsubscribe();
    }

    if (this.inlineEditEventsSubscription) {
      this.inlineEditEventsSubscription.unsubscribe();
    }
  }

  editOrOpenEditDialog(field: Field<any>) {
    // Don't set focus for advertised price field (has textarea that needs normal interaction)
    const isAdvertisedPriceField = field.config?.typeId === 'briefd.vehicle.advertised_sell_price_in_eur';
    
    if (!isAdvertisedPriceField || !this.priceChangeReasonRequired) {
      this.focusService.setFocus(field.config.typeId);
    }

    if (field.type === 'currency' && (field.currencyConversion && !field.currencyConversion.isDefault) && field.connectedField) {
      return this.openEditCurrencyDialog(field);
    }
    return this.enableEdit(field);
  }

  private openEditCurrencyDialog(field: Field<any>) {
    if (this.getFieldApi) {
      this.getFieldApi.getField(this.entityUuid, field.fieldUuid).subscribe((result) => {
        const fetchedField = result.data;
        const connectedField = result.data.connectedField;

        if (!connectedField) {
          return this.enableEdit(field);
        }

        const dialog = FieldCurrencyFormDialogComponent.open(this.matDialog, this.entityUuid, this.getFieldApi, fetchedField, connectedField);

        dialog.afterClosed().subscribe((updated) => {
          if (updated) {
            this.onUpdate.emit(updated);
          }
        });
      });
    } else if (!this.updateField && this.subFields) {
      let connectedField;
      let currencyField;

      for (const subFieldUuid of Object.keys(this.subFields)) {
        if (this.subFields[subFieldUuid].type === 'currency_conversion_rate') {
          connectedField = this.subFields[subFieldUuid];
          connectedField.value = this.field.connectedField.value ? this.field.connectedField.value : this.field.currencyConversion.conversionRate;
        } else if (this.subFields[subFieldUuid].type === 'currency') {
          currencyField = this.subFields[subFieldUuid];
          currencyField.value = this.field.value;
        }
      }

      const dialog = FieldCurrencyFormDialogComponent.open(this.matDialog, this.entityUuid, this.getFieldApi, currencyField, connectedField);

      dialog.afterClosed().subscribe((updated) => {
        if (updated) {
          updated.forEach((setField) => {
            if (setField.fieldUuid === this.field.fieldUuid) {
              this.field.value = setField.value;
            } else if (setField.fieldUuid === this.field.connectedField.fieldUuid) {
              this.field.connectedField.value = setField.value;
              this.field.currencyConversion.conversionRate = setField.value;
            }
          });
          this.onUpdate.emit(updated);
        }
      });
    } else if (!this.updateField) {

      const dialog = FieldCurrencyFormDialogComponent.open(this.matDialog, this.entityUuid, this.getFieldApi, this.field, this.field.connectedField);

      dialog.afterClosed().subscribe((updated) => {
        if (updated) {
          updated.forEach((setField) => {
            if (setField.fieldUuid === this.field.fieldUuid) {
              this.field.value = setField.value;
            } else if (setField.fieldUuid === this.field.connectedField.fieldUuid) {
              this.field.connectedField.value = setField.value;
              this.field.currencyConversion.conversionRate = setField.value;
            }
          });
          this.onUpdate.emit(updated);
        }
      });
    }
  }
}

interface FieldEdit {
  originalField: Field<any>;
  updatedField: Field<any>;
  formGroup: FormGroup;

  connectedField?: Field<any>;
}
